import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, Upload, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VideosManager() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [mode, setMode] = useState('youtube');
  const [formData, setFormData] = useState({ title: '', description: '', category: 'activite', youtube_url: '', thumbnail_url: '' });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchVideos = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/videos`);
      setVideos(response.data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  const handleYoutubeSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/videos`, formData, { withCredentials: true });
      toast.success('Vidéo YouTube ajoutée');
      resetForm();
      fetchVideos();
    } catch (error) { toast.error('Erreur'); }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Sélectionnez un fichier'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/videos/upload?title=${encodeURIComponent(formData.title)}&description=${encodeURIComponent(formData.description)}&category=${encodeURIComponent(formData.category)}`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Vidéo téléversée');
      resetForm();
      fetchVideos();
    } catch (error) { toast.error('Erreur lors du téléversement'); }
    finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette vidéo ?')) return;
    try {
      await axios.delete(`${API}/videos/${id}`, { withCredentials: true });
      toast.success('Vidéo supprimée');
      fetchVideos();
    } catch (error) { toast.error('Erreur'); }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', category: 'activite', youtube_url: '', thumbnail_url: '' });
    setFile(null);
    setShowForm(false);
  };

  const categorySelect = (
    <select
      value={formData.category}
      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
      className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
      data-testid="video-category-select"
    >
      <option value="activite">Activité</option>
      <option value="temoignage">Témoignage</option>
      <option value="formation">Formation FPT</option>
      <option value="evenement">Événement</option>
      <option value="autre">Autre</option>
    </select>
  );

  return (
    <div data-testid="videos-manager">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Gestion des Vidéos ({videos.length})
        </h2>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary inline-flex items-center" data-testid="add-video-button">
          <Plus size={20} className="mr-2" />
          {showForm ? 'Annuler' : 'Ajouter'}
        </button>
      </div>

      {showForm && (
        <div className="card mb-6">
          <div className="flex gap-2 mb-4">
            <button onClick={() => setMode('youtube')} className={`px-4 py-2 rounded-lg text-sm ${mode === 'youtube' ? 'bg-[#4A6B48] text-white' : 'border border-[#E5E3DB]'}`}>
              <LinkIcon size={16} className="inline mr-2" />YouTube
            </button>
            <button onClick={() => setMode('upload')} className={`px-4 py-2 rounded-lg text-sm ${mode === 'upload' ? 'bg-[#4A6B48] text-white' : 'border border-[#E5E3DB]'}`}>
              <Upload size={16} className="inline mr-2" />Téléverser
            </button>
          </div>

          {mode === 'youtube' ? (
            <form onSubmit={handleYoutubeSubmit}>
              <div className="space-y-4">
                <input type="text" placeholder="Titre" required value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
                <textarea placeholder="Description" required rows={3} value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
                {categorySelect}
                <input type="url" placeholder="URL YouTube (ex: https://youtube.com/watch?v=...)" required value={formData.youtube_url} onChange={(e) => setFormData({...formData, youtube_url: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
                <input type="url" placeholder="URL miniature (optionnel)" value={formData.thumbnail_url} onChange={(e) => setFormData({...formData, thumbnail_url: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
                <button type="submit" className="btn-primary">Ajouter</button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleUpload}>
              <div className="space-y-4">
                <input type="text" placeholder="Titre" required value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
                <textarea placeholder="Description" required rows={3} value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
                {categorySelect}
                <input type="file" accept="video/*" required onChange={(e) => setFile(e.target.files[0])} className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
                <button type="submit" disabled={uploading} className="btn-primary disabled:opacity-50">{uploading ? 'Téléversement...' : 'Téléverser'}</button>
              </div>
            </form>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4A6B48]"></div></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {videos.length === 0 ? (
            <p className="col-span-2 text-center text-[#5C665A] py-8">Aucune vidéo. Ajoutez la première vidéo.</p>
          ) : videos.map((video) => (
            <div key={video.video_id} className="card">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs font-medium text-[#D87D4A]">
                      {video.youtube_url ? 'YouTube' : 'Fichier téléversé'}
                    </span>
                    {video.category && (
                      <span className="text-xs font-bold px-2 py-0.5 bg-[#4A6B48]/10 text-[#4A6B48] rounded">
                        {video.category === 'temoignage' ? 'Témoignage' :
                          video.category === 'formation' ? 'Formation' :
                          video.category === 'evenement' ? 'Événement' :
                          video.category === 'autre' ? 'Autre' : 'Activité'}
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-bold text-[#2E332D] mt-1">{video.title}</h3>
                  <p className="text-sm text-[#5C665A] mt-1 line-clamp-2">{video.description}</p>
                </div>
                <button onClick={() => handleDelete(video.video_id)} className="p-2 hover:bg-gray-100 rounded">
                  <Trash2 size={18} className="text-red-500" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
